import FormItem from '@/components/Form/FormItem';
import { FormValidation } from '@/helpers/formValidations';
import { useGetAllCountries } from '@/hooks/dataProvider';
import { ErrorMessage } from '@hookform/error-message';
import React from 'react';
import { UseFormRegister, FieldErrors, Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import AppFormSelect from '../AppFormSelect';

type Props = {
  register: UseFormRegister<any>;
  errors: FieldErrors<any>;
  control: Control<any, any>;
};

export default function PassengerDetail({ register, errors, control }: Props) {
  const { t } = useTranslation();
  const { data: countries } = useGetAllCountries();

  const phoneCodeOptions = React.useMemo(() => {
    return countries?.data?.map(v => ({ value: v.phoneCode, label: `${v.name} (${v.phoneCode})` }));
  }, [countries]);
  const countryOptions = React.useMemo(() => {
    return countries?.data?.map(v => ({ value: v.shortCode, label: v.name }));
  }, [countries]);
  return (
    <div className="row x-gap-10 y-gap-20 mt-20">
      <div className="col-12  ">
        <div className="d-flex align-items-center">
          <div className="fw-bold text-18 text-blue-1  rounded-2 pr-10 text-uppercase">LEAD GUEST DETAILS</div>
          <div style={{ flex: 1, border: '1px dashed #cccccc' }} />
        </div>
        <div className="fw-500 text-14 text-red-1">Guest name must match the government-issued photo ID in Roman alphabet (A-Z).</div>
      </div>

      <div className="col-md-6 col-xs-12">
        <FormItem isRequired errors={errors} label={t('base.FirstName')} name="leadPassenger.firstName">
          <input {...register('leadPassenger.firstName.', FormValidation.RequiredWithRomanjiCharacter)} />
        </FormItem>
      </div>

      <div className="col-md-6 col-xs-12">
        <FormItem isRequired errors={errors} label={t('base.LastName')} name="leadPassenger.lastName">
          <input {...register('leadPassenger.lastName', FormValidation.RequiredWithRomanjiCharacter)} />
        </FormItem>
      </div>
      <div className="col-md-6 col-xs-12">
        <div className="form-item-select">
          <div className={`lh-1 text-14 text-light-1 label required`}>Nationality</div>
          <div className="select-container">
            <Controller
              control={control}
              rules={FormValidation.Required}
              render={({ field: { onChange, onBlur, value } }) => (
                <AppFormSelect
                  classNamePrefix="omega-select"
                  className="w-100"
                  options={countryOptions}
                  value={value}
                  onChange={onChange}
                  placeholder=""
                  onBlur={onBlur}
                />
              )}
              name="leadPassenger.countryCode"
            />
          </div>
        </div>
        <ErrorMessage errors={errors} name={'leadPassenger.countryCode'} as="span" className="form-item-error" />
      </div>

      <div className="col-md-6 col-xs-12">
        <FormItem errors={errors} label={t('base.Email')} name="leadPassenger.emailAddress">
          <input {...register('leadPassenger.emailAddress', FormValidation.Email)} />
        </FormItem>
      </div>

      <div className="col-md-6 col-xs-12">
        <div className="form-item-select">
          <div className={`lh-1 text-14 text-light-1 label`}>Country code</div>
          <div className="select-container">
            <Controller
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <AppFormSelect
                  classNamePrefix="omega-select"
                  className="w-100"
                  options={phoneCodeOptions}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder=""
                  isClearable
                />
              )}
              name="leadPassenger.phoneCode"
            />
          </div>
        </div>
        <ErrorMessage errors={errors} name={'leadPassenger.phoneCode'} as="span" className="form-item-error" />
      </div>
      <div className="col-md-6 col-xs-12">
        <FormItem errors={errors} label="Phone Number" name="leadPassenger.phoneNumber">
          <input {...register('leadPassenger.phoneNumber', FormValidation.PhoneNumber)} />
        </FormItem>
      </div>
    </div>
  );
}
